/**
 * Timezone Utilities
 *
 * Core functions for timezone conversion and formatting.
 * All internal operations use UTC; display uses user's local timezone.
 */

// ============================================================================
// Types
// ============================================================================

export interface TimezoneInfo {
  id: string;
  displayName: string;
  abbreviation: string;
  utcOffsetMinutes: number;
  hasDst: boolean;
}

export interface FormatOptions {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  hour12?: boolean;
}

export interface DateFormatPreferences {
  dateFormat: 'yyyy-MM-dd' | 'MM/dd/yyyy' | 'dd/MM/yyyy';
  timeFormat: '12h' | '24h';
  locale: string;
}

// ============================================================================
// Timezone Detection
// ============================================================================

/**
 * Get the user's system timezone (IANA identifier).
 * Falls back to UTC if detection fails.
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Check if a timezone identifier is valid.
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the current UTC offset in minutes for a timezone.
 * Positive values are ahead of UTC (e.g., +330 for IST).
 */
export function getUtcOffset(timezone: string, date: Date = new Date()): number {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  return Math.round((tzDate.getTime() - utcDate.getTime()) / 60000);
}

/**
 * Get timezone abbreviation (e.g., 'EST', 'PST', 'IST').
 */
export function getTimezoneAbbreviation(timezone: string, date: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    /* v8 ignore next */
    return tzPart?.value ?? timezone;
  } catch {
    /* v8 ignore next */
    return timezone;
  }
}

// ============================================================================
// Timezone Conversion
// ============================================================================

/**
 * Convert a UTC timestamp to local time in a specific timezone.
 * Returns a Date object adjusted for the timezone.
 */
export function toLocalTime(utcDate: Date | string | number, timezone: string): Date {
  const date = new Date(utcDate);

  // Get the UTC time string in the target timezone
  const localString = date.toLocaleString('en-US', { timeZone: timezone });
  return new Date(localString);
}

/**
 * Convert a local datetime in a specific timezone to UTC.
 * Use this when the user enters a datetime in their local timezone.
 */
export function toUtc(localDate: Date, timezone: string): Date {
  // Get the offset for the local date in the given timezone
  const offset = getUtcOffset(timezone, localDate);

  // Subtract the offset to get UTC
  return new Date(localDate.getTime() - offset * 60000);
}

/**
 * Convert between timezones without going through UTC explicitly.
 */
export function convertTimezone(
  date: Date | string | number,
  fromTimezone: string,
  toTimezone: string
): Date {
  const sourceDate = new Date(date);
  const utc = toUtc(sourceDate, fromTimezone);
  return toLocalTime(utc, toTimezone);
}

// ============================================================================
// Formatting
// ============================================================================

/**
 * Format a UTC date for display in a specific timezone.
 */
export function formatLocalDateTime(
  utcDate: Date | string | number,
  timezone: string,
  options: FormatOptions = {}
): string {
  const date = new Date(utcDate);

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    dateStyle: options.dateStyle ?? 'medium',
    timeStyle: options.timeStyle ?? 'short',
    hour12: options.hour12,
  };

  return date.toLocaleString(undefined, defaultOptions);
}

/**
 * Format a date with explicit timezone indicator.
 * Example: "Jan 15, 2024, 2:30 PM EST"
 */
export function formatWithTimezone(
  utcDate: Date | string | number,
  timezone: string,
  options: FormatOptions = {}
): string {
  const date = new Date(utcDate);

  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    dateStyle: options.dateStyle ?? 'medium',
    timeStyle: options.timeStyle ?? 'short',
    hour12: options.hour12,
    timeZoneName: 'short',
  });

  return formatter.format(date);
}

/**
 * Format only the date portion (no time).
 */
export function formatDate(
  date: Date | string | number,
  timezone: string,
  style: 'full' | 'long' | 'medium' | 'short' = 'medium'
): string {
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    timeZone: timezone,
    dateStyle: style,
  });
}

/**
 * Format only the time portion (no date).
 */
export function formatTime(
  date: Date | string | number,
  timezone: string,
  hour12: boolean = true
): string {
  const d = new Date(date);
  return d.toLocaleTimeString(undefined, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12,
  });
}

/**
 * Format a date according to user preferences.
 */
export function formatWithPreferences(
  date: Date | string | number,
  timezone: string,
  preferences: DateFormatPreferences
): string {
  const d = new Date(date);
  const isHour12 = preferences.timeFormat === '12h';

  // Map our date format to Intl options
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    hour12: isHour12,
    hour: 'numeric',
    minute: '2-digit',
  };

  switch (preferences.dateFormat) {
    case 'yyyy-MM-dd':
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
      break;
    case 'MM/dd/yyyy':
    case 'dd/MM/yyyy':
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = '2-digit';
      break;
    default:
      options.dateStyle = 'medium';
  }

  return d.toLocaleString(preferences.locale || undefined, options);
}

// ============================================================================
// Accounting Date Utilities
// ============================================================================

/**
 * Parse an accounting date string (YYYY-MM-DD) without timezone conversion.
 * Returns a Date at midnight UTC on that date.
 */
export function parseAccountingDate(dateString: string): Date {
  const parts = dateString.split('-').map(Number);
  /* v8 ignore next 3 */
  const year = parts[0] ?? 1970;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Format a date as an accounting date string (YYYY-MM-DD).
 * Uses the timezone to determine which calendar date to use.
 */
export function toAccountingDate(date: Date, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
}

/**
 * Get today's date as an accounting date string in the given timezone.
 */
export function getTodayAccountingDate(timezone: string): string {
  return toAccountingDate(new Date(), timezone);
}

/**
 * Check if two dates are the same calendar day in a given timezone.
 */
export function isSameDay(date1: Date, date2: Date, timezone: string): boolean {
  return toAccountingDate(date1, timezone) === toAccountingDate(date2, timezone);
}

// ============================================================================
// Relative Time
// ============================================================================

/**
 * Get a human-readable relative time string.
 * Example: "2 hours ago", "in 3 days"
 */
export function getRelativeTime(
  date: Date | string | number,
  locale: string = 'en'
): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSeconds) < 60) {
    return rtf.format(diffSeconds, 'second');
  } else if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  } else if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, 'day');
  } else if (Math.abs(diffDays) < 365) {
    return rtf.format(Math.round(diffDays / 30), 'month');
  } else {
    return rtf.format(Math.round(diffDays / 365), 'year');
  }
}

// ============================================================================
// Timezone Info
// ============================================================================

/**
 * Get detailed information about a timezone.
 */
export function getTimezoneInfo(timezone: string, date: Date = new Date()): TimezoneInfo {
  const offset = getUtcOffset(timezone, date);
  const abbreviation = getTimezoneAbbreviation(timezone, date);

  // Check for DST by comparing offsets in January and July
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  const janOffset = getUtcOffset(timezone, jan);
  const julOffset = getUtcOffset(timezone, jul);
  const hasDst = janOffset !== julOffset;

  // Format display name
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  const offsetStr = `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  // Create readable display name
  const displayName = `(${offsetStr}) ${timezone.replace(/_/g, ' ').replace(/\//g, ' / ')}`;

  return {
    id: timezone,
    displayName,
    abbreviation,
    utcOffsetMinutes: offset,
    hasDst,
  };
}

/**
 * Format a UTC offset as a string like "+05:30" or "-08:00".
 */
export function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
